import { useAppSelector, useAppDispatch } from '../reduxProvider'
import {
  UseTenantUsersResult,
  TenantUsersRequestParams,
  TenantUsersListResponse,
  CreateTenantUserType,
} from './types'
import { setList, clearList, showLoader, hideLoader } from './store'
import useFetch from '../../utils/fetch/useFetch'
import useError from '../../utils/errors/useError'

const useTenant = (): UseTenantUsersResult => {
  const dispatch = useAppDispatch()
  const {
    list,
    page,
    pageSize,
    availablePages,
    availableItems,
    search,
    isLoaded,
  } = useAppSelector((state) => state.tenantUsers)
  const { get, post, del, patch } = useFetch()
  const { notifyApiError } = useError()

  const errorModel = 'admin'
  const endpoint = 'tenant-admin-users'

  const parseParams = (
    requestParams?: TenantUsersRequestParams
  ): TenantUsersRequestParams => ({
    page_size: pageSize,
    search: requestParams?.search ?? search,
    page: requestParams?.page ?? page,
  })

  const getList = async (params?: TenantUsersRequestParams) => {
    const response: TenantUsersListResponse | undefined = await get({
      endpoint,
      params,
    })
    return response
  }

  const fetchList = async (params?: TenantUsersRequestParams) => {
    dispatch(showLoader())
    const reqParams = parseParams(params)
    const response: TenantUsersListResponse | undefined = await getList(
      reqParams
    )
    response &&
      dispatch(
        setList({
          search: reqParams.search as string,
          list: response.items,
          page: response.page,
          pageSize: response.pageSize,
          availableItems: response.availableItems,
          availablePages: response.availablePages,
        })
      )
    dispatch(hideLoader())
  }

  const createUser = async (user: CreateTenantUserType) => {
    try {
      await post({ endpoint, body: user })
    } catch (e) {
      notifyApiError(e, errorModel, 'create')
    }
  }

  const updateUser = async (id: string, profiles: string[]) => {
    try {
      await patch({ endpoint, id, body: { profiles } })
    } catch (e) {
      notifyApiError(e, errorModel, 'edit')
    }
  }

  const deleteUser = async (id: string) => {
    try {
      await del({ endpoint, id })
    } catch (e) {
      notifyApiError(e, errorModel, 'delete')
    }
  }

  return {
    list,
    page,
    availablePages,
    availableItems,
    pageSize,
    search,
    isLoaded,
    fetchList,
    clearList: () => dispatch(clearList()),
    createUser,
    updateUser,
    deleteUser,
  }
}

export default useTenant
